import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import type { ProjectRole } from '../../types/project';

export interface Invitation {
  id: string;
  projectId: string;
  projectName: string;
  email: string;
  fromAccountFullName: string;
  role: ProjectRole;
  isAccepted: boolean | null;
  createdAt: number;
}

export function useInvitations(projectId: string) {
  return useQuery<Invitation[]>({
    queryKey: ['invitations', projectId],
    queryFn: () => api.get<Invitation[]>(`/invite/project/${projectId}`),
    enabled: !!projectId,
  });
}

export function useCreateInvitation(projectId: string) {
  return useToastMutation(
    ['invitations', projectId],
    (invite: { email: string; role: string; roleName: string }) =>
      api.post<Invitation>(`/invite/project/${projectId}`, {
        projectId,
        email: invite.email,
        role: invite.role,
        frontendUrl: window.location.origin,
      }),
    {
      success: (_d, v) => ({ title: 'Invite Sent', description: `${v.email} as ${v.roleName}`, timeout: 5000 }),
    }
  );
}

export function useDeleteInvitation(projectId: string) {
  // Errors surface via the global handler.
  return useToastMutation(
    ['invitations', projectId],
    (inviteId: string) => api.delete(`/invite/${inviteId}/project/${projectId}`),
  );
}

export interface ReviewerRequest {
  projectId: string;
  startDate: string;
  endDate: string;
  numReviewers: number;
}

export function useRequestReviewer(projectId: string) {
  return useToastMutation(
    ['invitations', projectId],
    (req: ReviewerRequest) => api.post(`/invite/project/${projectId}/reviewer`, req),
    {
      success: () => ({ title: 'Reviewer Request Sent', description: 'A representative will be contacting you shortly.', timeout: 5000 }),
    }
  );
}
